import { getBillingCycle } from "CMS/utils";
import { Panel } from "components/panels";
import currency from "currency.js";
import moment from "moment";
import React from "react";
import { formatCurrency, formatShortDate } from "utilities";

interface Props {
  internalUsers: number;
  billingCycle: server.dto.BillingCycles;
  discountPercentage: server.dto.Percentage;
  discountUntil: Date;
  basePricePerMonth: number;
  pricePerUserPerMonth: number;
  currency: server.dto.Currencies;
  title: string;
  pricingAppliesFrom: moment.Moment;
}

const SubscriptionPricingPanel = (props: Props) => {
  const billingCycle = getBillingCycle(props.billingCycle);

  const basePrice = currency(props.basePricePerMonth).multiply(billingCycle.months);
  const pricePerUser = currency(props.pricePerUserPerMonth).multiply(billingCycle.months);

  const totalUserPrice = pricePerUser.multiply(props.internalUsers);

  // Calculate total price before any discounts
  const totalBeforeDiscount = basePrice.add(totalUserPrice);

  // Calculate price after discount
  let totalAfterDiscount = totalBeforeDiscount;

  // if discountUntil is null, the discount is permanent
  // otherwise we make sure the discount is still valid when this pricing applies
  const hasDiscount = props.discountPercentage != null && (props.discountUntil == null || moment.utc(props.discountUntil).isAfter(props.pricingAppliesFrom));

  if (hasDiscount) {
    totalAfterDiscount = totalBeforeDiscount.multiply(1 - props.discountPercentage.value);
  }

  const internalUserLabel = props.internalUsers === 1
    ? `1 ${RESX.UserType.InternalUser}`
    : `${props.internalUsers} ${RESX.UserType.InternalUsers}`
    ;

  const discountedAmount = totalBeforeDiscount.subtract(totalAfterDiscount);

  return <Panel
    title={props.title}
    panelClassName="s1_related-feature-panel">
    <table>
      <tbody>
        <tr>
          <td>{RESX.Subscription.BasePrice}</td>
          <td className="s1_cell_financialamount">
            {formatCurrency(basePrice, props.currency, serverReference.culture.uiLocale)}
          </td>
        </tr>
        <tr>
          <td style={{ whiteSpace: "nowrap", paddingRight: 15 }}>{internalUserLabel} * {formatCurrency(pricePerUser, props.currency, serverReference.culture.uiLocale)}</td>
          <td className="s1_cell_financialamount">
            {formatCurrency(totalUserPrice, props.currency, serverReference.culture.uiLocale)}
          </td>
        </tr>
        {hasDiscount && <tr>
          <td>
            <div>{RESX.Subscription.Discount} {props.discountPercentage.text}</div>
            {props.discountUntil != null && <div className="text-sublabel">{RESX.DateTime.lblUntil} {formatShortDate(moment.utc(props.discountUntil))}</div>}
          </td>
          <td className="s1_cell_financialamount">
            {formatCurrency(-discountedAmount, props.currency, serverReference.culture.uiLocale)}
          </td>
        </tr>}
        <tr style={{ fontWeight: "bold" }}>
          <td style={{ paddingTop: 10 }}>{billingCycle.text}</td>
          <td style={{ paddingTop: 10 }} className="s1_cell_financialamount">
            {formatCurrency(totalAfterDiscount, props.currency, serverReference.culture.uiLocale)}
          </td>
        </tr>
      </tbody>
    </table>
  </Panel>
}

export default SubscriptionPricingPanel;